import { useState, useEffect } from "react";
import type { Relationship } from "@/types/model/Roles";
import localStoreService from "@/services/middleware/storageService";
import { getRelationshipSpecificToken } from "@/services/api/RolesPermissionsRepository";
import { getRelationships } from "@/services/service-layer/relationshipsService";
import type {
  ChangeActiveRelation,
  UseRelationships,
  AuthAccountResponse,
} from "./use-relationships.types";
import { useRelationshipsStore } from "@/zustand/relationshipsStore";

/**
 * Provides all available info about user relationships with different accounts &
 * helpful, effectful helpers.
 *
 * @returns Object containing information about relationships.
 */
const useRelationships: UseRelationships = () => {
  // Needed to reactify localStorage item
  const { relationships: allAvailableRelationships } = useRelationshipsStore(
    (state) => state
  );
  const [activeRelationshipId, setActiveRelationshipId] = useState<
    number | null
  >(localStoreService.getItem("activeRelationshipId"));
  const availableActiveRelationships = allAvailableRelationships;
  const activeRelationship =
    allAvailableRelationships?.find(
      (relationship: Relationship) => relationship?.id === activeRelationshipId
    ) || null;

  useEffect(() => {
    if (allAvailableRelationships?.length === 0) {
      getRelationships();
    }
  }, [allAvailableRelationships]);

  /**
   * Helper to change current active relation
   *
   * @param {number} newRelationshipId Id of relationship to set as active
   *
   * @returns {Promise} A promise that either resolves to `/auth/account/{id}` payload
   * or rejects with Axios error or RelationAuthError error.
   */
  const changeActiveRelation: ChangeActiveRelation = (newRelationshipId) =>
    newRelationshipId
      ? getRelationshipSpecificToken(newRelationshipId)
          .then((response: AuthAccountResponse) => {
            if (response.status !== "OK") {
              throw new Error("RelationAuthError");
            }
            setActiveRelationshipId(newRelationshipId);
            localStoreService.setItem(
              "activeRelationshipId",
              newRelationshipId
            );
            localStoreService.setItem("token", response.token.hash);

            return response;
          })
          .catch((error) => {
            throw error;
          })
      : null;

  return {
    activeRelationship,
    allAvailableRelationships,
    availableActiveRelationships,
    changeActiveRelation,
  };
};

export default useRelationships;
